import { useState, useMemo } from "react";

const PoliciesPage = () => {
  /* ── state ──────────────────────────────────────────────── */
  const [tab, setTab] = useState("freight");

  /* ── effective date (DD-MM-YYYY) ────────────────────────── */
  const rawDate =
    typeof window !== "undefined" ? localStorage.getItem("create_date") : null;

  const effectiveDate = useMemo(() => {
    if (!rawDate) return "—";
    const d = new Date(rawDate);
    return `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1
    ).padStart(2, "0")}-${d.getFullYear()}`;
  }, [rawDate]);

  /* ── POLICIES ───────────────────────────────────────────── */
  const freightPolicy = (
    <div className="space-y-6">
      {/* 1. Overview */}
      <section className="bg-gray-50 p-6 rounded-lg shadow-md space-y-4">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
          1. Overview
        </h2>
        <p className="text-gray-700 leading-relaxed">
          This Freight Policy governs the shipping and delivery of automobile
          parts purchased and sold via the <strong>Wreckonnect</strong>{" "}
          platform. It outlines the responsibilities of{" "}
          <strong>Suppliers, Customers</strong>, and{" "}
          <strong>Wreckonnect</strong> regarding the dispatch, transit, and
          receipt of goods.
        </p>
        <p className="text-gray-700 leading-relaxed">
          Suppliers are responsible for securely packaging and dispatching goods
          within the agreed timeframe. Customers must provide accurate delivery
          details and inspect goods upon receipt.
        </p>
        <p className="text-red-700 leading-relaxed font-semibold">
          Wreckonnect acts as a platform facilitator and is not directly
          responsible for freight execution but may assist with coordination and
          dispute resolution.
        </p>
      </section>

      {/* 2. Freight Responsibilities */}
      <section className="bg-gray-50 p-6 rounded-lg shadow-md space-y-4 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
          2. Freight Responsibilities
        </h2>
        <ul className="list-disc list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>
            <strong>Suppliers</strong> are responsible for securely packaging
            and dispatching goods within the agreed timeframe.
          </li>
          <li>
            <strong>Customers</strong> must provide accurate delivery details
            and inspect goods upon receipt.
          </li>
          <li>
            <strong>Wreckonnect</strong> acts as a <b>platform facilitator</b>{" "}
            and is not directly responsible for freight execution, but may
            assist with coordination and dispute resolution.
          </li>
        </ul>
      </section>

      {/* 3. Freight Methods */}
      <section className="bg-gray-50 p-6 rounded-lg shadow-md space-y-4 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
          3. Freight Methods
        </h2>
        <ul className="list-disc list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>
            Standard courier services
            <span className="text-sm text-blue-600 font-medium">
              {" "}
              (e.g. Australia Post, Aramex)
            </span>
          </li>
          <li>
            Freight carriers
            <span className="text-sm text-green-600 font-medium">
              {" "}
              (for large or heavy parts)
            </span>
          </li>
          <li>
            Express shipping
            <span className="text-sm text-purple-600 font-medium">
              {" "}
              (if offered by the supplier)
            </span>
          </li>
          <li>
            Local pickup
            <span className="text-sm text-gray-600 italic">
              {" "}
              (if enabled by the supplier)
            </span>
          </li>
        </ul>
        <p className="text-gray-700 leading-relaxed">
          The chosen method depends on{" "}
          <b>item size, delivery location, and supplier options</b>.
        </p>
      </section>

      {/* 4. Freight Charges */}
      <section className="bg-gray-50 p-6 rounded-lg shadow-md space-y-4 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
          4. Freight Charges
        </h2>
        <ul className="list-disc list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>
            Calculated based on <b>weight, size, distance,</b> and{" "}
            <b>delivery method</b>.
          </li>
          <li>
            Charges may be included in <b>product price, added at checkout,</b>{" "}
            or <b>handled separately by the supplier</b>
            <span className="text-sm text-gray-600 italic">
              {" "}
              (for offline arrangements)
            </span>
            .
          </li>
          <li>
            Customers will be informed of freight costs before order
            confirmation.
          </li>
        </ul>
      </section>

      {/* 5. Dispatch & Delivery Timeframes */}
      <section className="bg-gray-50 p-6 rounded-lg shadow-md space-y-4 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
          5. Dispatch & Delivery Timeframes
        </h2>
        <ul className="list-disc list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>
            Suppliers must dispatch orders within <b>2 business days</b> unless
            specified otherwise.
          </li>
          <li>
            Metro areas: <b>2–5 business days</b>
          </li>
          <li>
            Regional/Rural areas: <b>4–10 business days</b>
          </li>
        </ul>
        <p className="text-gray-700 leading-relaxed">
          Delivery times may vary due to courier availability, public holidays,
          or delays.
        </p>
      </section>

      {/* 6. Freight Damage or Loss */}
      <section className="bg-gray-50 p-6 rounded-lg shadow-md space-y-4 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
          6. Freight Damage or Loss
        </h2>
        <ul className="list-disc list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>Inspect goods immediately upon delivery.</li>
          <li>
            <b>Visible damage:</b> Report to courier and notify Wreckonnect
            within <b>48 h</b> with photos.
          </li>
          <li>
            <b>Concealed damage or missing items:</b> Report to Wreckonnect and
            supplier within <b>3 days</b>.
          </li>
          <li>
            Wreckonnect will facilitate communication between parties, but is
            not liable for issues related to third-party carriers.
          </li>
        </ul>
      </section>

      {/* 7. Returns & Freight */}
      <section className="bg-gray-50 p-6 rounded-lg shadow-md space-y-4 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
          7. Returns & Freight
        </h2>
        <ul className="list-disc list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>
            <b>Customer-initiated returns:</b> Customer pays return freight
            unless agreed otherwise.
          </li>
          <li>
            <b>Supplier error or defect:</b> Supplier covers return shipping
            costs.
          </li>
          <li>All returns must follow the platform’s Return Policy.</li>
        </ul>
      </section>

      {/* 8. Special Freight Conditions */}
      <section className="bg-gray-50 p-6 rounded-lg shadow-md space-y-4 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
          8. Special Freight Conditions
        </h2>
        <p className="text-gray-700 leading-relaxed">
          For oversized or fragile items (e.g., forklifts, tail-lift trucks),
          customers must notify Wreckonnect or the supplier at time of purchase.
        </p>
      </section>

      {/* 9. International Shipping */}
      <section className="bg-gray-50 p-6 rounded-lg shadow-md space-y-4 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
          9. International Shipping
        </h2>
        <p className="text-gray-700 leading-relaxed">
          Currently, Wreckonnect facilitates shipments within <b>Australia</b>{" "}
          only. Any international shipping requests must be handled directly
          between the supplier and customer outside the platform.
        </p>
      </section>

      {/* 10. Contact */}
      <section className="bg-gray-50 p-6 rounded-lg shadow-md space-y-4 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
          10. Contact
        </h2>
        <div className="text-gray-700 leading-relaxed space-y-2">
          <p>
            <b>
              Wreckonnect
              <br />
              Gisborne, Victoria AUS 3437
            </b>
          </p>
          <p>
            📧 <b>Phone:</b> 0419 3820 250
          </p>
          <p>
            📧 <b>Email:</b>{" "}
            <a
              href="mailto:admin@wreckonnect.com.au"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              admin@wreckonnect.com.au{" "}
            </a>
          </p>
        </div>
      </section>
    </div>
  );

  const privacyPolicy = (
    <div className="space-y-6">
      {/* 1. Information We Collect */}
      <section className="bg-gray-50 p-6 rounded-lg shadow-md space-y-4 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
          1. Information We Collect
        </h2>
        <ul className="list-disc list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>
            <strong>Personal Information:</strong> Name, phone, email,
            communication history.
          </li>
          <li>
            <strong>Vehicle Part Requests:</strong> Car model, required parts,
            and specs.
          </li>
          <li>
            <strong>Location Information:</strong> Only if enabled for delivery.
          </li>
          <li>
            <strong>Device Information:</strong> Device model, OS version, crash
            logs.
          </li>
        </ul>
      </section>

      {/* 2. How We Use Your Information */}
      <section className="bg-gray-50 p-6 rounded-lg shadow-md space-y-4 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
          2. How We Use Your Information
        </h2>
        <ul className="list-disc list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>Facilitate request and quote processes.</li>
          <li>Enable communication and order tracking.</li>
          <li>Improve app functionality and performance.</li>
          <li>Support inquiries and manage account issues.</li>
        </ul>
      </section>

      {/* 3. Payment Methods */}
      <section className="bg-gray-50 p-6 rounded-lg shadow-md space-y-4 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
          3. Payment Methods
        </h2>
        <ul className="list-disc list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>
            <strong>Account (Offline):</strong> A direct payment arrangement
            between customers and suppliers. Payment details are shared
            privately and handled outside the app via bank transfers or cash.
          </li>
          <li>
            <strong>Capricorn (Offline):</strong> A specialized offline payment
            method used by selected suppliers and customers based on
            pre-established agreements.
          </li>
          <li>
            <strong>Stripe (Online):</strong> Wreckonnect integrates with Stripe
            for secure digital payments. All transactions are encrypted via
            Stripe’s PCI-compliant system; no financial data is stored on our
            servers.
          </li>
        </ul>
        <p className="text-gray-700 leading-relaxed">
          These options ensure flexibility while maintaining transparency and
          security.
        </p>
      </section>

      {/* 4. Data Sharing */}
      <section className="bg-gray-50 p-6 rounded-lg shadow-md space-y-4 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
          4. Data Sharing
        </h2>
        <p className="text-gray-700 leading-relaxed">
          We do not sell or rent data. We only share with:
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>Suppliers to process your vehicle-part requests.</li>
          <li>Trusted service providers to operate and maintain the app.</li>
          <li>Authorities if legally required.</li>
        </ul>
      </section>

      {/* 5. Data Security */}
      <section className="bg-gray-50 p-6 rounded-lg shadow-md space-y-4 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
          5. Data Security
        </h2>
        <p className="text-gray-700 leading-relaxed">
          We apply standard security practices to protect your data. While no
          system is 100 % secure, we take reasonable precautions.
        </p>
      </section>

      {/* 6. Your Rights */}
      <section className="bg-gray-50 p-6 rounded-lg shadow-md space-y-4 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
          6. Your Rights
        </h2>
        <p className="text-gray-700 leading-relaxed">
          You may contact us anytime to request access to or deletion of your
          personal data. We honor lawful requests promptly.
        </p>
      </section>

      {/* 7. Children's Privacy */}
      <section className="bg-gray-50 p-6 rounded-lg shadow-md space-y-4 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
          7. Children's Privacy
        </h2>
        <p className="text-gray-700 leading-relaxed">
          Wreckonnect is not intended for users under 13 and we do not knowingly
          collect their data.
        </p>
      </section>

      {/* 8. Changes */}
      <section className="bg-gray-50 p-6 rounded-lg shadow-md space-y-4 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
          8. Changes to This Policy
        </h2>
        <p className="text-gray-700 leading-relaxed">
          We may update this policy as needed. Changes will appear here with an
          updated effective date.
        </p>
      </section>

      {/* 9. Contact */}
      <section className="bg-gray-50 p-6 rounded-lg shadow-md space-y-4 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
          9. Contact Us
        </h2>
        <p className="text-gray-700 leading-relaxed">Questions? Reach out:</p>
        <p className="text-gray-700">
          📧 <b>Email:</b>{" "}
          <a
            href="mailto:admin@wreckonnect.com.au"
            className="text-blue-600 hover:underline"
          >
            admin@wreckonnect.com.au
          </a>
        </p>
        <p className="text-gray-700">
          🌐 <b>Website:</b>{" "}
          <a
            href="https://www.wreckonnect.com.au/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            https://www.wreckonnect.com.au/{" "}
          </a>
        </p>
      </section>
    </div>
  );

  /* ── RENDER ─────────────────────────────────────────────── */
  return (
    <>
      <style>{`
      .no-scrollbar::-webkit-scrollbar { display: none; }
      .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
    `}</style>

      <main className="min-h-screen w-full bg-white">
        <div
          className="w-full max-w-5xl mx-auto            /* readable column */
             h-full rounded-xl shadow
              overflow-y-auto overflow-x-hidden       /* kill horizontal scroll */
              px-4 sm:px-6 lg:px-8
              break-words" /* wrap long URLs/words */
        >
          {" "}
          <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b px-6 py-4 sticky top-0 bg-white z-10">
            <nav className="flex gap-2">
              {[
                { id: "freight", label: "Freight Policy" },
                { id: "privacy", label: "Privacy Policy" },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => setTab(id)}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                    tab === id
                      ? "bg-blue-600 text-white shadow"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {label}
                </button>
              ))}
            </nav>

            <p className="text-xs font-medium text-gray-500">
              Effective Date: {effectiveDate}
            </p>
          </header>
          {/* ─── body ────────────────────────────── */}
          <section className="px-6 py-8 space-y-8">
            {tab === "freight" ? freightPolicy : privacyPolicy}
          </section>
        </div>
      </main>
    </>
  );
};

export default PoliciesPage;
